
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/utils/PageTransition';
import { Layers, ArrowLeft, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AIModelsPage = () => {
  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-32 pb-20">
          <div className="container mx-auto px-6">
            <div className="mb-6">
              <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to all services
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-12 mb-16">
              <div className="md:col-span-2">
                <div className="animate-fade-in">
                  <div className="bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400 p-3 rounded-xl inline-block mb-4">
                    <Layers className="h-10 w-10" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                    AI Models | <span className="text-primary">Foundational Models</span>
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Pre-trained and custom foundational models to power your AI applications.
                  </p>
                </div>

                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    Foundational models are large-scale AI models trained on vast datasets that can be adapted for a wide range of applications. Our AI model solutions help businesses leverage the power of these models to create intelligent applications quickly and cost-effectively.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We provide access to state-of-the-art foundational models and help you adapt them to your specific use cases through fine-tuning and customization. Our approach focuses on making advanced AI accessible and practical for businesses of all sizes.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Leverage pre-trained models to reduce development time and cost</li>
                    <li>Customize models for your specific domain and use cases</li>
                    <li>Improve model performance through fine-tuning and optimization</li>
                    <li>Access cutting-edge AI capabilities without extensive expertise</li>
                    <li>Deploy models efficiently across various platforms and environments</li>
                    <li>Stay at the forefront of AI innovation with continuous model updates</li>
                  </ul>
                </div>
              </div>

              <div className="md:col-span-1">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32">
                  <h3 className="text-xl font-semibold mb-4">Our AI Model Services</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Large Language Models</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Vision Transformers</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Multimodal Models</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Model Fine-tuning</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Model Optimization</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Custom Model Development</span>
                    </li>
                  </ul>

                  <div className="mt-8">
                    <Link to="/contact">
                      <Button className="w-full">
                        Contact Us
                        <ArrowUpRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-950/30 dark:to-red-950/30 p-8 rounded-2xl mb-16">
              <h2 className="text-2xl font-semibold mb-4">Ready to leverage foundational models?</h2>
              <p className="text-muted-foreground mb-6">
                Let's discuss how our AI model solutions can help you build intelligent applications quickly and cost-effectively.
              </p>
              <Link to="/contact">
                <Button>
                  Get Started
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default AIModelsPage;
